<template>
  <div
    ref="marker-container"
    :class="{
      'tab-navigation-fi': true,
      'tab-navigation-fi--line-hidden': lineHidden,
      'tab-navigation-fi--shadow-left': showShadowLeft,
      'tab-navigation-fi--shadow-right': showShadowRight,
      'tab-navigation-fi--no-spacing-below': noSpacingBelow,
      'tab-navigation-fi--left': left,
      'tab-navigation-fi--full-width-tabs': fullWidthTabs,
      'tab-navigation-fi--grid': grid,
      [`tab-navigation-fi--theme-${theme}`]: theme,
    }"
  >
    <component
      :is="grid ? 'grid-columns-fi' : 'div'"
      ref="tabsContainer"
      :grid-base-class="grid ? 'fi-gap-zero' : null"
      :class="{
        'tab-navigation-fi__tabs-container': true,
        'tab-navigation-fi__tabs-container--tab-extra': $slots['tab-extra'],
      }"
    >
      <splide
        ref="tabNaviSlider"
        :options="sliderOptions"
        :has-track="false"
        class="tab-navigation-fi__tabs"
        @splide:dragging="handleShadows"
        @splide:resized="refreshShadows"
      >
        <splide-track>
          <splide-slide
            v-for="(item, idx) in tabNavigation"
            :key="item.id"
            :class="{
              'tab-navigation-fi__tab': true,
              'tab-navigation-fi__tab--active': active === item.id,
              'marker-mixin-marked': active === item.id,
              'tab-navigation-fi__tab--last': idx === tabNavigation.length - 1,
              'tab-navigation-fi__tab--hidden': item.hidden,
              'tab-navigation-fi__tab--icon': item.icon,
              [`tab-navigation-fi__tab--id-${item.id}`]: true,
            }"
            @click="changeTab(item.id, idx)"
          >
            <span class="tab-navigation-fi__tab-content marker-mixin-content">
              <icon-fi
                v-if="item.icon"
                class="tab-navigation-fi__tab-icon"
                :icon="item.icon"
              />
              <!-- eslint-disable vue/no-v-html -->
              <template
                v-if="
                  item.text &&
                  typeof item.text !== 'string' &&
                  (item.text.editable || item.text.value)
                "
              >
                <span
                  v-if="isEditMode && item.text.editable"
                  v-html="item.text.editable"
                />
                <span
                  v-else-if="!isEditMode && item.text.value"
                  v-html="item.text.value"
                />
              </template>
              <!-- eslint-enable vue/no-v-html -->
              <template v-else>
                {{ item.text }}
              </template>
            </span>
          </splide-slide>
          <li
            ref="marker"
            class="tab-navigation-fi__marker"
          />
        </splide-track>
      </splide>
      <div
        v-if="$slots['tab-extra']"
        class="tab-navigation-fi__tab-extra"
      >
        <slot
          name="tab-extra"
          :active="active"
        />
      </div>
    </component>

    <div
      class="tab-navigation-fi__arrow tab-navigation-fi__arrow-prev"
      @click="goPrev"
    >
      <icon-fi icon="chevron-left" />
    </div>

    <div
      class="tab-navigation-fi__arrow tab-navigation-fi__arrow-next"
      @click="goNext"
    >
      <icon-fi icon="chevron-right" />
    </div>

    <slot name="note" />

    <template v-if="!isJumpMarkNavigation">
      <div
        v-if="contentLoading"
        :class="{
          'tab-navigation-fi__content': true,
          'tab-navigation-fi__content--dark': dark,
          'tab-navigation-fi__loading': true,
        }"
      >
        <loading-indicator
          variant="spinnerLocal"
          class="spinner-local--rebrush"
          :dark="true"
        />
      </div>

      <transition-group
        v-else
        enter-active-class="fade-in"
        leave-active-class="fade-out"
      >
        <template
          v-for="item in renderedTabs"
          :key="item.id"
        >
          <div v-show="!hideOnly || active === item.id">
            <component
              :is="grid ? 'grid-columns-fi' : 'div'"
              :class="{
                'tab-navigation-fi__content': true,
                'tab-navigation-fi__content--dark': dark,
              }"
            >
              <slot
                v-if="$slots[`tab-content-${item.id}`]"
                :name="`tab-content-${item.id}`"
                :is-active="active === item.id"
              />
              <slot
                v-else
                name="tab-content-default"
                :item-id="item.id"
                :is-active="active === item.id"
                ><span class="fi-hidden">missing slot {{ item.id }}</span></slot
              >
            </component>
          </div>
        </template>
      </transition-group>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { IconFi, LoadingIndicator } from 'atoms';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import { useMarker } from '@/composables/marker';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';

export interface TabNavigation {
  id: number;
  hidden?: boolean;
  icon?: string;
  text: SingleLineTextField | string;
}

defineOptions({
  name: 'TabNavigationFi',
});

const props = withDefaults(
  defineProps<{
    tabNavigation?: TabNavigation[];
    contentLoading?: boolean;
    lineHidden?: boolean;
    refreshTabs?: boolean;
    initId?: number;
    noSpacingBelow?: boolean;
    enableScrollIfOutOfBoundsOnly?: boolean;
    theme?: string;
    hideOnly?: boolean;
    left?: boolean;
    dark?: boolean;
    fullWidthTabs?: boolean;
    grid?: boolean;
  }>(),
  {
    tabNavigation: () => [],
    initId: 0,
    theme: '',
  },
);

const emit = defineEmits<{
  change: [value: TabNavigation];
}>();

const { animateMarker } = useMarker();

// Sub pixel rounding, so tabs that exactly fill the row are not taken for an
// overflow.
const OVERFLOW_TOLERANCE = 1;

const activeId = ref<number | null>(null);
// Assumed until measured, which keeps the tabs draggable while the browser has
// not laid them out yet.
const isOverflow = ref<boolean>(true);
const showShadowLeft = ref<boolean>(false);
const showShadowRight = ref<boolean>(false);
const showShadowWidth = ref<number>(0);

let resizeObserver: ResizeObserver | undefined;

const tabNaviSlider = useTemplateRef('tabNaviSlider');
const tabsContainer = useTemplateRef<HTMLDivElement | typeof GridColumnsFi>('tabsContainer');

const sliderOptions = computed(() => ({
  arrows: false,
  autoWidth: true,
  drag: isOverflow.value,
}));

const active = computed<number>(() => {
  if (activeId.value || activeId.value === 0) {
    return activeId.value;
  }

  return props.initId;
});

/**
 * The tabs whose content is rendered. Filtering here rather than putting a `v-if` on the children
 * of the `transition-group` below is what keeps hydration intact: TransitionGroup drops comment
 * vnodes from its slot, the SSR compiler does not, so a switched-off `v-if` child leaves a
 * `<!---->` in the server markup that the client never accounts for - a hydration mismatch Vue
 * reports without naming a location.
 */
const renderedTabs = computed<TabNavigation[]>(() =>
  props.hideOnly
    ? props.tabNavigation
    : props.tabNavigation.filter((item) => item.id === active.value),
);

const activeItem = computed<TabNavigation | undefined>(() =>
  props.tabNavigation?.find((item) => item.id === active.value),
);

const tabs = computed<any[]>(() => tabNaviSlider.value?.splide?.Components?.Slides?.get());

const isJumpMarkNavigation = computed(() => props.theme === 'jump-marks');

const goPrev = () => {
  tabNaviSlider.value?.splide?.go('<');
  setTimeout(handleShadows, 500);
};

const goNext = () => {
  tabNaviSlider.value?.splide?.go('>');
  setTimeout(handleShadows, 500);
};

/**
 * Whether the tabs are wider than the row they get. Measured from the tab
 * elements instead of asked of Splide, so the answer does not depend on how
 * far the slider happens to be scrolled.
 */
const syncOverflow = () => {
  if (IS_SSR) {
    return;
  }

  const root: HTMLElement | undefined = tabNaviSlider.value?.$el;
  if (!root) {
    return;
  }

  const tabElements = root.querySelectorAll<HTMLElement>('.tab-navigation-fi__tab');
  const firstTab = tabElements[0];
  const lastTab = tabElements[tabElements.length - 1];

  if (!firstTab || !lastTab) {
    return;
  }

  const tabsWidth = lastTab.getBoundingClientRect().right - firstTab.getBoundingClientRect().left;

  isOverflow.value = tabsWidth > root.getBoundingClientRect().width + OVERFLOW_TOLERANCE;
};

/**
 * Splide re-measures on window resize only, which is not enough to decide
 * whether the tabs are draggable: a row that starts out in a collapsed
 * container would never learn that it got its width, and would stay locked.
 */
const observeOverflow = () => {
  const root: HTMLElement | undefined = tabNaviSlider.value?.$el;

  if (!root || typeof ResizeObserver === 'undefined') {
    return;
  }

  resizeObserver = new ResizeObserver(syncOverflow);
  resizeObserver.observe(root);

  // Depending on whether splide's core css is on the page the list is either
  // content sized or track sized. Observing it costs nothing in the second
  // case and catches tabs that grow on their own in the first.
  const list = root.querySelector('.splide__list');
  if (list) {
    resizeObserver.observe(list);
  }
};

const executeRefreshTabs = async () => {
  if (IS_SSR) {
    return;
  }
  await nextTick();
  animateMarker();
  syncOverflow();
  refreshShadows();
};

watch(() => props.refreshTabs, executeRefreshTabs);
watch(() => props.tabNavigation, executeRefreshTabs, { deep: true });

const changeTab = async (id: number, idx: number) => {
  const isDirectionRight = activeId.value === null || activeId.value < id;
  const nextIndex: number = isDirectionRight ? idx : idx - 1;
  handleNavigationPosition(nextIndex, isDirectionRight);

  activeId.value = id;

  if (!props.enableScrollIfOutOfBoundsOnly) {
    setPosition(idx);
  }

  await nextTick();
  animateMarker();
  await nextTick();
  handleShadows();
  emit('change', props.tabNavigation[id]);
};

const setPosition = (index: number) => tabNaviSlider.value?.splide?.go(index);

const handleNavigationPosition = (nextIndex: number, isDirectionRight: boolean) => {
  if (!props.enableScrollIfOutOfBoundsOnly || IS_SSR) {
    return;
  }

  const activeSlideElement = tabs.value?.[nextIndex]?.slide;
  const nextSlideElement = tabs.value?.[isDirectionRight ? nextIndex + 1 : nextIndex]?.slide;

  if (!activeSlideElement) {
    return;
  }

  const tabSliderDimensions = tabNaviSlider.value?.$el?.getBoundingClientRect();
  const activeSlideDimensions = activeSlideElement.getBoundingClientRect();
  const nextSlideEdgeRight =
    nextSlideElement?.getBoundingClientRect().left +
    nextSlideElement?.getBoundingClientRect().width;

  const tabSliderEdge = {
    left: tabSliderDimensions.left,
    right: tabSliderDimensions.left + tabSliderDimensions.width,
  };
  const activeSlideEdge = {
    left: activeSlideDimensions.left,
    right: activeSlideDimensions.left + activeSlideDimensions.width,
  };

  if (activeSlideEdge.right > tabSliderEdge.right) {
    // Update Tabs position in direction right
    setPosition(nextIndex - 1);
  } else if (
    activeSlideEdge.left < tabSliderEdge.left &&
    activeSlideEdge.right !== tabSliderEdge.left
  ) {
    // Update Tabs position in direction left
    let width = activeSlideDimensions.width + showShadowWidth.value;

    tabs.value.every((item, idx) => {
      width += item.slide.offsetWidth;

      // add the width until the sum is greater than the maximum available space
      if (width > tabSliderEdge.right && nextSlideEdgeRight < tabSliderEdge.left) {
        setPosition((activeId.value || 0) - 1 - idx);
        return false;
      }

      return true;
    });
  }
};

const handleShadows = () => {
  if (IS_SSR) {
    return;
  }
  const tabSliderDimensions = tabNaviSlider.value?.$el?.getBoundingClientRect();
  if (!tabSliderDimensions) {
    return;
  }

  const firstTab = tabs.value?.[0]?.slide;
  const lastTab = tabs.value?.[tabs.value.length - 1]?.slide;

  if (firstTab?.getBoundingClientRect().left < tabSliderDimensions.left) {
    showShadowLeft.value = true;
  } else {
    showShadowLeft.value = false;
  }

  if (
    lastTab?.getBoundingClientRect().left + lastTab?.getBoundingClientRect().width >
    tabSliderDimensions.left + tabSliderDimensions.width
  ) {
    showShadowRight.value = true;
  } else {
    showShadowRight.value = false;
  }

  if (!tabsContainer.value) {
    return;
  }
  // timeout is necessary because there is a css transition on the shadow pseudo elements
  setTimeout(() => {
    if (!tabsContainer.value) {
      console.debug('no tabs container found');
      return;
    }
    const tabsElement: HTMLElement =
      // @ts-ignore TODO
      tabsContainer.value.$el !== undefined ? tabsContainer.value.$el : tabsContainer.value;
    const shadowWidthLeft = Number.parseInt(
      window?.getComputedStyle(tabsElement, ':before').width,
      10,
    );
    const shadowWidthRight = Number.parseInt(
      window?.getComputedStyle(tabsElement, ':after').width,
      10,
    );
    showShadowWidth.value = shadowWidthLeft || shadowWidthRight;
  }, 650);
};

/**
 * Splide repositions the list from a listener of its own resize event that is
 * registered after this component's, so the shadows can only be measured a
 * task later - before that the list is still where it was.
 */
const refreshShadows = () => {
  setTimeout(handleShadows, 0);
};

onMounted(async () => {
  window.addEventListener('load', animateMarker);

  await nextTick();
  animateMarker();
  observeOverflow();

  // Web fonts land after the first measurement and change how wide the tabs
  // are without touching the row around them.
  document.fonts?.ready.then(syncOverflow);

  // Unfortunately necessary
  setTimeout(() => {
    syncOverflow();
    handleShadows();
  }, 0);
});

onBeforeUnmount(() => {
  window.removeEventListener('load', animateMarker);
  resizeObserver?.disconnect();
  resizeObserver = undefined;
});

defineExpose({ activeItem, changeTab, activeId, active });
</script>

<style src="./tab-navigation-fi-unscoped" />

<style scoped src="./tab-navigation-fi" />
